import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUserPermissions } from '../hooks/useUserPermissions';
import { useAccessApi, Organization, SsoConnectionStatus } from '../services/api';
import { useAdminCompany } from '../hooks/useApi';
import { auth0Config } from '../config/auth0';
import { LoadingPage } from './LoadingSpinner';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Button } from './ui/button';
import {
    ArrowLeft,
    Copy,
    ExternalLink,
    Settings,
    Trash2,
    Clock,
    AlertTriangle,
} from 'lucide-react';

interface SelfServiceProfile {
    id: string;
    name: string;
    description?: string;
    branding?: unknown;
    user_attributes?: unknown;
    allowed_strategies?: string[];
}

interface ExistingTicket {
    ticketUrl: string;
    ticketId: string;
    issuedAt: string;
    expiresAt: string;
    organizationDisplayName: string;
}

export function SsoAdmin() {
    const { orgId: routeOrgId, companyId } = useParams<{ orgId?: string; companyId?: string }>();
    const numericCompanyId = companyId ? Number(companyId) : null;
    const companyQuery = useAdminCompany(
        numericCompanyId != null && Number.isFinite(numericCompanyId) && numericCompanyId > 0
            ? numericCompanyId
            : null,
    );
    const orgId = routeOrgId ?? companyQuery.data?.organizationId ?? undefined;
    const isCompanyScoped = Boolean(companyId);
    const { hasPermission } = useUserPermissions();
    const {
        getOrganizations,
        getSelfServiceProfiles,
        getSSOTicket,
        revokeSSOTicket,
        generateSSOTicket,
        getSSOConnectionStatus,
    } = useAccessApi();
    const navigate = useNavigate();
    const [organization, setOrganization] = useState<Organization | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Multi-step wizard state (in modal)
    const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1);
    const [profiles, setProfiles] = useState<SelfServiceProfile[]>([]);
    const [selectedProfile, setSelectedProfile] = useState<SelfServiceProfile | null>(null);
    const [isLoadingProfiles, setIsLoadingProfiles] = useState(false);
    const [profilesError, setProfilesError] = useState<string | null>(null);

    // Ticket generation state
    const [ticketUrl, setTicketUrl] = useState<string>('');
    const [isGeneratingTicket, setIsGeneratingTicket] = useState(false);
    const [ticketError, setTicketError] = useState<string | null>(null);
    const [copySuccess, setCopySuccess] = useState(false);

    // Domain aliases state
    const [domainAliases, setDomainAliases] = useState<string>('');
    const [domainAliasError, setDomainAliasError] = useState<string | null>(null);

    // Existing ticket state
    const [existingTicket, setExistingTicket] = useState<ExistingTicket | null>(null);
    const [isLoadingExistingTicket, setIsLoadingExistingTicket] = useState(true);
    const [isRevokingTicket, setIsRevokingTicket] = useState(false);
    const [existingTicketCopySuccess, setExistingTicketCopySuccess] = useState(false);

    // Confirmation modal state
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);

    // SSO Connection status state
    const [ssoConnectionStatus, setSsoConnectionStatus] = useState<SsoConnectionStatus | null>(
        null,
    );
    const [isLoadingConnectionStatus, setIsLoadingConnectionStatus] = useState(true);
    const [connectionStatusError, setConnectionStatusError] = useState<string | null>(null);

    // Construct Auth0 management URL
    const match = auth0Config.domain.match(/^auth-(.*?)\.wevo\.ai$/);
    const auth0OrgUrl = match ? `https://manage.auth0.com/dashboard/us/wevo-${match[1]}` : null;

    // Fetch organization details
    useEffect(() => {
        const fetchOrganization = async () => {
            if (!orgId) return;

            try {
                setIsLoading(true);
                setError(null);

                // Get organization details
                const orgs = await getOrganizations();
                const org = orgs.find((o) => o.id === orgId);
                if (!org) {
                    setError('Organization not found.');
                    return;
                }
                setOrganization(org);
            } catch (err) {
                console.error('Error fetching organization data:', err);
                setError('Failed to load organization data.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchOrganization();
    }, [orgId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch existing SSO ticket
    useEffect(() => {
        const fetchExistingTicket = async () => {
            if (!orgId) return;

            try {
                setIsLoadingExistingTicket(true);
                const response = await getSSOTicket(orgId);

                if (response.success && response.hasTicket && response.ticket) {
                    setExistingTicket(response.ticket);
                } else {
                    setExistingTicket(null);
                }
            } catch (err) {
                console.error('Error fetching existing SSO ticket:', err);
                setExistingTicket(null);
            } finally {
                setIsLoadingExistingTicket(false);
            }
        };

        fetchExistingTicket();
    }, [orgId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Function to fetch SSO connection status (extracted for reuse)
    const fetchConnectionStatus = async () => {
        if (!orgId) return;

        try {
            setIsLoadingConnectionStatus(true);
            setConnectionStatusError(null);

            const response = await getSSOConnectionStatus(orgId);
            if (response.success) {
                setSsoConnectionStatus(response.data);
            } else {
                setConnectionStatusError('Failed to check SSO connection status.');
            }
        } catch (err) {
            console.error('Error fetching SSO connection status:', err);
            setConnectionStatusError('Failed to check SSO connection status.');
        } finally {
            setIsLoadingConnectionStatus(false);
        }
    };

    // Fetch SSO connection status on component mount/orgId change
    useEffect(() => {
        fetchConnectionStatus();
    }, [orgId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-revoke existing ticket when SSO connection is active
    useEffect(() => {
        const autoRevokeTicketIfConnectionActive = async () => {
            // Only proceed if we have both states loaded and SSO connection exists
            if (
                !isLoadingConnectionStatus &&
                !isLoadingExistingTicket &&
                ssoConnectionStatus?.exists &&
                existingTicket &&
                orgId
            ) {
                try {
                    console.log('SSO connection is active - auto-revoking existing ticket');
                    await revokeSSOTicket(orgId);

                    // Clear the existing ticket from state since it's been revoked
                    setExistingTicket(null);

                    console.log('Successfully auto-revoked SSO ticket');
                } catch (err) {
                    console.error('Error auto-revoking SSO ticket:', err);
                    // Don't show error to user since this is automatic cleanup
                }
            }
        };

        autoRevokeTicketIfConnectionActive();
    }, [
        isLoadingConnectionStatus,
        isLoadingExistingTicket,
        ssoConnectionStatus,
        existingTicket,
        orgId,
        revokeSSOTicket,
    ]);

    // Only show this component if user has administrate:organizations permission
    if (!hasPermission('administrate:organizations')) {
        return null;
    }

    const handleBackToAdmin = () => {
        if (isCompanyScoped && companyId) {
            navigate(`/admin/companies/${companyId}`);
            return;
        }
        navigate(`/admin/${orgId}`);
    };

    const handleOpenModal = () => {
        setIsModalOpen(true);
        handleStartWizard();
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        // Reset wizard state
        setCurrentStep(1);
        setProfiles([]);
        setSelectedProfile(null);
        setProfilesError(null);
        setTicketUrl('');
        setTicketError(null);
        setCopySuccess(false);
        setDomainAliases('');
        setDomainAliasError(null);
    };

    // Handle successful completion of SSO setup (refresh states)
    const handleSuccessfulCompletion = async () => {
        // Close modal and reset wizard state
        handleCloseModal();

        // Refresh connection status to show new SSO connection
        await fetchConnectionStatus();

        // Also refresh existing ticket state in case it was auto-revoked
        if (orgId) {
            try {
                setIsLoadingExistingTicket(true);
                const response = await getSSOTicket(orgId);

                if (response.success && response.hasTicket && response.ticket) {
                    setExistingTicket(response.ticket);
                } else {
                    setExistingTicket(null);
                }
            } catch (err) {
                console.error('Error fetching existing SSO ticket after completion:', err);
                setExistingTicket(null);
            } finally {
                setIsLoadingExistingTicket(false);
            }
        }
    };

    const handleStartWizard = async () => {
        setIsLoadingProfiles(true);
        setProfilesError(null);
        setCurrentStep(1);

        try {
            const response = await getSelfServiceProfiles();
            setProfiles(response.profiles);
        } catch (err) {
            console.error('Failed to fetch Self-Service Profiles:', err);
            setProfilesError(err instanceof Error ? err.message : 'Failed to fetch profiles');
        } finally {
            setIsLoadingProfiles(false);
        }
    };

    const handleProfileSelect = (profile: SelfServiceProfile) => {
        setSelectedProfile(profile);
        setCurrentStep(2);
    };

    const handleBackToProfileSelection = () => {
        setCurrentStep(1);
        setTicketUrl('');
        setTicketError(null);
    };

    const handleContinueToGenerate = () => {
        // Validate domain aliases before proceeding
        const validation = validateDomainAliases(domainAliases);
        if (!validation.isValid) {
            setDomainAliasError(validation.error || 'Invalid domain aliases');
            return;
        }

        setCurrentStep(3);
        setTicketError(null);
        setDomainAliasError(null);
    };

    const handleBackToDomainAliases = () => {
        setCurrentStep(2);
        setTicketUrl('');
        setTicketError(null);
    };

    // Validate and clean domain aliases
    const validateDomainAliases = (
        input: string,
    ): { isValid: boolean; cleanedDomains: string[]; error?: string } => {
        if (!input.trim()) {
            return { isValid: true, cleanedDomains: [] }; // Empty is valid (optional)
        }

        const domains = input
            .split(',')
            .map((domain) => domain.trim())
            .map((domain) => domain.replace(/^@/, '')) // Remove leading @ if present
            .filter((domain) => domain.length > 0);

        // Basic domain validation regex
        const domainRegex =
            /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

        const invalidDomains = domains.filter((domain) => !domainRegex.test(domain));

        if (invalidDomains.length > 0) {
            return {
                isValid: false,
                cleanedDomains: [],
                error: `Invalid domain format: ${invalidDomains.join(', ')}. Use domain names like 'company.com' (no @ symbols)`,
            };
        }

        return { isValid: true, cleanedDomains: domains };
    };

    const handleDomainAliasChange = (value: string) => {
        setDomainAliases(value);

        // Clear error when user starts typing
        if (domainAliasError) {
            setDomainAliasError(null);
        }
    };

    const handleGenerateTicket = async () => {
        if (!orgId || !selectedProfile) return;

        setIsGeneratingTicket(true);
        setTicketError(null);

        try {
            // Validate and clean domain aliases
            const validation = validateDomainAliases(domainAliases);
            if (!validation.isValid) {
                setTicketError(validation.error || 'Invalid domain aliases');
                return;
            }

            const response = await generateSSOTicket(
                orgId,
                selectedProfile.id,
                validation.cleanedDomains,
            );
            setTicketUrl(response.ticketUrl);
        } catch (err) {
            console.error('Failed to generate SSO ticket:', err);

            // Try to parse the enhanced error response from the backend
            let errorMessage = 'Failed to generate SSO ticket';
            let errorDetails: string[] = [];

            if (err instanceof Error) {
                // Check if this is a structured error response from our backend
                try {
                    // The API service throws the error.message from the response
                    // For our enhanced errors, this will be the user-friendly message
                    errorMessage = err.message;

                    // For connection already exists error, provide helpful guidance
                    if (errorMessage.includes('already exists')) {
                        errorDetails = [
                            'This organization may already have SSO configured.',
                            'Check your existing connections in the Auth0 dashboard.',
                            'Contact support if you need to update an existing connection.',
                        ];
                    } else if (errorMessage.includes('invalid data')) {
                        errorDetails = [
                            'Please check your domain aliases format.',
                            'Domain aliases should be in the format: company.com',
                            'Multiple domains should be separated by commas.',
                        ];
                    }
                } catch (parseError) {
                    console.error('Error parsing backend response:', parseError);
                }
            }

            // Create a more detailed error message
            let fullErrorMessage = errorMessage;
            if (errorDetails.length > 0) {
                fullErrorMessage += '\n\nSuggested actions:\n• ' + errorDetails.join('\n• ');
            }

            setTicketError(fullErrorMessage);
        } finally {
            setIsGeneratingTicket(false);
        }
    };

    const handleCopyUrl = async () => {
        if (!ticketUrl) return;

        try {
            await navigator.clipboard.writeText(ticketUrl);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch (err) {
            console.error('Failed to copy URL:', err);
        }
    };

    const handleCopyExistingUrl = async () => {
        if (!existingTicket) return;

        try {
            await navigator.clipboard.writeText(existingTicket.ticketUrl);
            setExistingTicketCopySuccess(true);
            setTimeout(() => setExistingTicketCopySuccess(false), 2000);
        } catch (err) {
            console.error('Failed to copy existing ticket URL:', err);
        }
    };

    const handleRevokeTicket = async () => {
        if (!orgId || !existingTicket) return;

        try {
            setIsRevokingTicket(true);
            await revokeSSOTicket(orgId);

            // Clear the existing ticket from state
            setExistingTicket(null);

            // Show success message (you could add a toast notification here)
            console.log('SSO ticket revoked successfully');
        } catch (err) {
            console.error('Failed to revoke SSO ticket:', err);
            // You could show an error message here
        } finally {
            setIsRevokingTicket(false);
        }
    };

    const handleSetupSSOClick = () => {
        if (existingTicket) {
            // Show confirmation modal if there's an existing ticket
            setShowConfirmationModal(true);
        } else {
            // No existing ticket, proceed directly
            handleOpenModal();
        }
    };

    const handleConfirmNewTicket = () => {
        setShowConfirmationModal(false);
        handleOpenModal();
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString();
    };

    if (isCompanyScoped && companyQuery.isSuccess && !companyQuery.data?.organizationId) {
        return (
            <div className="space-y-6">
                <Card>
                    <CardHeader className="bg-muted/40">
                        <CardTitle className="text-xs text-muted-foreground uppercase tracking-wider">
                            SSO Management
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                        <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
                            <p className="text-amber-800">
                                Sync this company to Auth0 before managing SSO.
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if ((isCompanyScoped && companyQuery.isLoading) || isLoading || isLoadingConnectionStatus) {
        return (
            <div className="space-y-6">
                <Card>
                    <CardHeader className="bg-muted/40">
                        <CardTitle className="text-xs text-muted-foreground uppercase tracking-wider">
                            SSO Management
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                        <LoadingPage />
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (error || companyQuery.isError) {
        return (
            <div className="space-y-6">
                <Card>
                    <CardHeader className="bg-muted/40">
                        <CardTitle className="text-xs text-muted-foreground uppercase tracking-wider">
                            SSO Management
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                        <div className="bg-red-50 border border-red-200 rounded-md p-4">
                            <p className="text-red-800">
                                {error ?? 'Failed to load company SSO data.'}
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="flex items-start gap-3">
                    <Button variant="ghost" size="icon" onClick={handleBackToAdmin}>
                        <ArrowLeft className="size-5" />
                    </Button>
                    <div>
                        <p className="text-sm text-neutral-500">Company SSO</p>
                        <h1 className="text-3xl font-semibold text-neutral-950">Manage SSO</h1>
                        <p className="mt-1 text-sm text-neutral-500">
                            {companyQuery.data?.name ||
                                organization?.display_name ||
                                organization?.name}
                        </p>
                    </div>
                </div>
            </div>

            {/* Main SSO Configuration Card */}

            <Card>
                <CardHeader className="bg-muted/40">
                    <CardTitle className="text-xs text-muted-foreground uppercase tracking-wider">
                        Self-Service SSO Configuration
                    </CardTitle>
                    <CardDescription>
                        Set up Single Sign-On for{' '}
                        {organization?.display_name || organization?.name || 'this organization'}.
                        Generate secure configuration tickets that can be sent to organization
                        administrators to complete their SSO setup.
                    </CardDescription>
                </CardHeader>

                <CardContent className="p-6">
                    {/* Existing Ticket Section */}
                    {isLoadingExistingTicket ? (
                        <div className="flex items-center justify-center py-4">
                            <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                            <span className="ml-2 text-sm text-muted-foreground">
                                Checking for existing tickets...
                            </span>
                        </div>
                    ) : existingTicket && !ssoConnectionStatus?.exists ? (
                        <div className="mb-8 p-4 border border-green-200 bg-green-50 rounded-lg">
                            <div className="flex items-start justify-between">
                                <div className="flex-1">
                                    <h4 className="text-sm font-semibold text-green-800 mb-2">
                                        Active SSO Configuration Ticket
                                    </h4>
                                    <div className="space-y-2 text-sm text-green-700">
                                        <div className="flex items-center gap-2">
                                            <Clock className="w-4 h-4" />
                                            <span>
                                                Created: {formatDate(existingTicket.issuedAt)}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Clock className="w-4 h-4" />
                                            <span>
                                                Expires: {formatDate(existingTicket.expiresAt)}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="mt-3 p-2 bg-green-100 rounded text-xs font-mono text-green-800 break-all">
                                        {existingTicket.ticketUrl}
                                    </div>
                                </div>
                            </div>
                            <div className="flex gap-2 mt-4">
                                <Button
                                    onClick={handleCopyExistingUrl}
                                    variant="outline"
                                    size="sm"
                                    className="text-green-700 border-green-200 hover:bg-green-100"
                                >
                                    <Copy className="w-4 h-4 mr-2" />
                                    {existingTicketCopySuccess ? 'Copied!' : 'Copy URL'}
                                </Button>
                                <Button
                                    onClick={handleRevokeTicket}
                                    variant="outline"
                                    size="sm"
                                    disabled={isRevokingTicket}
                                    className="text-red-700 border-red-200 hover:bg-red-50"
                                >
                                    {isRevokingTicket ? (
                                        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                                    ) : (
                                        <Trash2 className="w-4 h-4 mr-2" />
                                    )}
                                    {isRevokingTicket ? 'Revoking...' : 'Revoke'}
                                </Button>
                            </div>
                        </div>
                    ) : null}

                    {/* SSO Status Section */}
                    {connectionStatusError ? (
                        <div className="text-center py-12">
                            <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
                                <p className="text-red-800">{connectionStatusError}</p>
                            </div>
                        </div>
                    ) : ssoConnectionStatus?.exists ? (
                        /* Existing SSO Connection */
                        <div className="text-center py-12">
                            <div className="w-20 h-20 mx-auto mb-6 bg-green-50 rounded-full flex items-center justify-center">
                                <Settings className="w-10 h-10 text-green-600" />
                            </div>
                            <h3 className="text-xl font-semibold text-foreground mb-4">
                                SSO Connection Active
                            </h3>
                            <div className="bg-green-50 border border-green-200 rounded-md p-6 mb-6 max-w-2xl mx-auto">
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center">
                                        <span className="font-medium text-gray-700">
                                            Connection Name:
                                        </span>
                                        <span className="text-gray-900 font-mono text-sm">
                                            {ssoConnectionStatus.connection?.id && auth0OrgUrl ? (
                                                <a
                                                    href={`${auth0OrgUrl}/connections/enterprise/waad/${ssoConnectionStatus.connection?.id}/settings`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer inline-flex items-center gap-1"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        window.open(
                                                            `${auth0OrgUrl}/connections/enterprise/waad/${ssoConnectionStatus.connection?.id}/settings`,
                                                            '_blank',
                                                        );
                                                        e.stopPropagation();
                                                    }}
                                                >
                                                    {ssoConnectionStatus.connectionName}
                                                    <ExternalLink className="w-3 h-3" />
                                                </a>
                                            ) : (
                                                ssoConnectionStatus.connectionName
                                            )}
                                        </span>
                                    </div>
                                    {ssoConnectionStatus.connection && (
                                        <>
                                            <div className="flex justify-between items-center">
                                                <span className="font-medium text-gray-700">
                                                    Strategy:
                                                </span>
                                                <span className="text-gray-900 uppercase">
                                                    {ssoConnectionStatus.connection.strategy}
                                                </span>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                            <p className="text-muted-foreground max-w-lg mx-auto">
                                Your organization already has an SSO connection configured. Users
                                can sign in using your identity provider.
                            </p>
                        </div>
                    ) : !existingTicket ? (
                        /* Setup SSO Section - only show when no connection AND no existing ticket */
                        <div className="text-center py-12">
                            <div className="w-20 h-20 mx-auto mb-6 bg-blue-50 rounded-full flex items-center justify-center">
                                <Settings className="w-10 h-10 text-blue-600" />
                            </div>
                            <h3 className="text-xl font-semibold text-foreground mb-6">
                                Configure Organization SSO
                            </h3>

                            <Button
                                onClick={handleSetupSSOClick}
                                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 mb-6 mx-auto"
                                size="lg"
                            >
                                <ExternalLink className="w-5 h-5" />
                                Setup SSO Ticket
                            </Button>

                            <p className="text-muted-foreground max-w-lg mx-auto">
                                Generate secure SSO configuration tickets using Auth0 Self-Service
                                Profiles. These tickets can be shared with organization
                                administrators to set up their identity provider integrations.
                            </p>
                        </div>
                    ) : null}
                </CardContent>
            </Card>

            {/* How It Works Card */}
            <Card>
                <CardHeader className="bg-muted/40">
                    <CardTitle className="text-xs text-muted-foreground uppercase tracking-wider">
                        How It Works
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                    <div className="space-y-2">
                        <p className="text-sm text-muted-foreground">
                            <span className="font-medium text-foreground">Step 1:</span> Select the
                            appropriate Self-Service Profile that matches your organization's SSO
                            requirements.
                        </p>
                        <p className="text-sm text-muted-foreground">
                            <span className="font-medium text-foreground">Step 2:</span> Configure
                            domain aliases for your SSO connection (optional).
                        </p>
                        <p className="text-sm text-muted-foreground">
                            <span className="font-medium text-foreground">Step 3:</span> Generate a
                            secure configuration ticket that expires in 5 days and can only be used
                            once.
                        </p>
                        <p className="text-sm text-muted-foreground">
                            <span className="font-medium text-foreground">Step 4:</span> Copy the
                            generated URL and send it to your organization administrator to complete
                            the SSO setup.
                        </p>
                    </div>
                </CardContent>
            </Card>

            {/* Confirmation Modal */}
            {showConfirmationModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
                        <div className="p-6">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                                    <AlertTriangle className="w-6 h-6 text-orange-600" />
                                </div>
                                <h3 className="text-lg font-semibold text-foreground">
                                    Replace Existing Ticket?
                                </h3>
                            </div>
                            <p className="text-sm text-muted-foreground mb-6">
                                An active SSO configuration ticket already exists for this
                                organization. Generating a new ticket will automatically deactivate
                                the existing one. Are you sure you want to continue?
                            </p>
                            <div className="flex gap-3">
                                <Button
                                    onClick={() => setShowConfirmationModal(false)}
                                    variant="outline"
                                    className="flex-1"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleConfirmNewTicket}
                                    className="flex-1 bg-orange-600 hover:bg-orange-700"
                                >
                                    Continue
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal for SSO Configuration Wizard */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                        {/* Modal Header */}
                        <div className="flex items-center justify-between p-6 border-b">
                            <div className="flex items-center gap-4">
                                <h2 className="text-xl font-bold text-foreground">
                                    SSO Configuration Wizard
                                </h2>
                                <div className="flex items-center gap-2">
                                    <div
                                        className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${currentStep >= 1 ? 'bg-blue-600 text-white' : 'bg-muted text-muted-foreground'}`}
                                    >
                                        1
                                    </div>
                                    <div className="w-8 h-0.5 bg-muted" />
                                    <div
                                        className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${currentStep >= 2 ? 'bg-blue-600 text-white' : 'bg-muted text-muted-foreground'}`}
                                    >
                                        2
                                    </div>
                                    <div className="w-8 h-0.5 bg-muted" />
                                    <div
                                        className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${currentStep >= 3 ? 'bg-blue-600 text-white' : 'bg-muted text-muted-foreground'}`}
                                    >
                                        3
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={ticketUrl ? handleSuccessfulCompletion : handleCloseModal}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <svg
                                    className="w-6 h-6"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            </button>
                        </div>

                        {/* Modal Content */}
                        <div className="p-6">
                            {/* Step 1: Profile Selection */}
                            {currentStep === 1 && (
                                <div className="space-y-6">
                                    <div className="text-center">
                                        <div className="w-16 h-16 mx-auto mb-4 bg-muted/40 rounded-lg flex items-center justify-center">
                                            <svg
                                                className="w-8 h-8 text-muted-foreground"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                                                />
                                            </svg>
                                        </div>
                                        <h3 className="text-lg font-semibold text-foreground mb-2">
                                            Select Self-Service Profile
                                        </h3>
                                        <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                                            Choose the Self-Service Profile configuration that will
                                            be used to generate the SSO setup ticket.
                                        </p>

                                        {profilesError && (
                                            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                                                <p className="text-red-800 text-sm">
                                                    {profilesError}
                                                </p>
                                            </div>
                                        )}

                                        {isLoadingProfiles && (
                                            <div className="flex items-center justify-center gap-2">
                                                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                                Loading Profiles...
                                            </div>
                                        )}
                                    </div>

                                    {profiles.length > 0 && (
                                        <div className="space-y-3">
                                            <h4 className="text-sm font-semibold text-foreground">
                                                Available Profiles
                                            </h4>
                                            {profiles.map((profile) => (
                                                <div
                                                    key={profile.id}
                                                    className="border rounded-lg p-4 hover:bg-muted/20 cursor-pointer transition-colors"
                                                    onClick={() => handleProfileSelect(profile)}
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex-1">
                                                            <h5 className="text-sm font-medium text-foreground">
                                                                {profile.name}
                                                            </h5>
                                                            {profile.description && (
                                                                <p className="text-xs text-muted-foreground mt-1">
                                                                    {profile.description}
                                                                </p>
                                                            )}
                                                        </div>
                                                        <Button size="sm" variant="outline">
                                                            Select
                                                        </Button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Step 2: Domain Aliases Configuration */}
                            {currentStep === 2 && selectedProfile && (
                                <div className="space-y-6">
                                    {/* Selected Profile Info */}
                                    <div className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
                                        <div>
                                            <h4 className="text-sm font-medium">
                                                Selected Profile: {selectedProfile.name}
                                            </h4>
                                            {selectedProfile.description && (
                                                <p className="text-xs text-muted-foreground">
                                                    {selectedProfile.description}
                                                </p>
                                            )}
                                        </div>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={handleBackToProfileSelection}
                                        >
                                            Change
                                        </Button>
                                    </div>

                                    {/* Domain Aliases Configuration */}
                                    <div className="text-center">
                                        <div className="w-16 h-16 mx-auto mb-4 bg-muted/40 rounded-lg flex items-center justify-center">
                                            <svg
                                                className="w-8 h-8 text-muted-foreground"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0 9c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3 1.34 3 3 3zm0-9V3c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"
                                                />
                                            </svg>
                                        </div>
                                        <h3 className="text-lg font-semibold text-foreground mb-6">
                                            Configure Domain Aliases
                                        </h3>
                                    </div>

                                    <div className="space-y-4">
                                        <div>
                                            <label
                                                htmlFor="domainAliases"
                                                className="block text-sm font-medium text-foreground mb-2"
                                            >
                                                Domain Aliases (Optional)
                                            </label>
                                            <input
                                                id="domainAliases"
                                                type="text"
                                                value={domainAliases}
                                                onChange={(e) =>
                                                    handleDomainAliasChange(e.target.value)
                                                }
                                                placeholder="e.g., company.com, app.company.com (no @ symbols)"
                                                className={`w-full px-3 py-2 border rounded-md admin-field-fill text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:border-transparent ${
                                                    domainAliasError
                                                        ? 'border-red-500 focus:ring-red-500'
                                                        : 'border-input focus:ring-blue-500'
                                                }`}
                                            />
                                            {domainAliasError && (
                                                <p className="text-xs text-red-600 mt-1">
                                                    {domainAliasError}
                                                </p>
                                            )}
                                            {!domainAliasError && (
                                                <p className="text-xs text-muted-foreground mt-1">
                                                    Comma-separated list of verified domains that
                                                    can be authenticated in the identity provider.
                                                    Use domain names only (no @ symbols).
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    <p className="text-muted-foreground max-w-md mx-auto text-center">
                                        Domain aliases allow users from these verified domains to
                                        authenticate using this SSO connection. Leave blank if not
                                        required.
                                    </p>

                                    {/* Navigation Buttons */}
                                    <div className="flex justify-between pt-4">
                                        <Button
                                            variant="outline"
                                            onClick={handleBackToProfileSelection}
                                        >
                                            Back
                                        </Button>
                                        <Button
                                            onClick={handleContinueToGenerate}
                                            className="bg-blue-600 hover:bg-blue-700"
                                        >
                                            Continue
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {/* Step 3: Generate Ticket */}
                            {currentStep === 3 && selectedProfile && (
                                <div className="space-y-6">
                                    {/* Selected Profile Info */}
                                    <div className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
                                        <div>
                                            <h4 className="text-sm font-medium">
                                                Selected Profile: {selectedProfile.name}
                                            </h4>
                                            {selectedProfile.description && (
                                                <p className="text-xs text-muted-foreground">
                                                    {selectedProfile.description}
                                                </p>
                                            )}
                                        </div>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={handleBackToDomainAliases}
                                        >
                                            Change
                                        </Button>
                                    </div>

                                    {/* Domain Aliases Summary */}
                                    {domainAliases && (
                                        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                            <p className="text-sm text-blue-800">
                                                <span className="font-medium">Domain Aliases:</span>{' '}
                                                {domainAliases}
                                            </p>
                                        </div>
                                    )}

                                    {/* Generate Ticket Section - Hide when ticket is generated */}
                                    {!ticketUrl && (
                                        <div className="text-center">
                                            <div className="w-16 h-16 mx-auto mb-4 bg-muted/40 rounded-lg flex items-center justify-center">
                                                <svg
                                                    className="w-8 h-8 text-muted-foreground"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                                    />
                                                </svg>
                                            </div>
                                            <h3 className="text-lg font-semibold text-foreground mb-6">
                                                Generate SSO Configuration Ticket
                                            </h3>

                                            <Button
                                                onClick={handleGenerateTicket}
                                                disabled={isGeneratingTicket}
                                                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 mb-6 mx-auto"
                                            >
                                                {isGeneratingTicket ? (
                                                    <>
                                                        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                                        Generating Ticket...
                                                    </>
                                                ) : (
                                                    <>
                                                        <ExternalLink className="w-4 h-4" />
                                                        Generate SSO Configuration Ticket
                                                    </>
                                                )}
                                            </Button>

                                            <p className="text-muted-foreground max-w-md mx-auto">
                                                Create a secure configuration link using the
                                                selected profile that can be sent to the
                                                organization administrator to set up their SSO
                                                integration.
                                            </p>

                                            {ticketError && (
                                                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
                                                    <div className="flex">
                                                        <div className="flex-shrink-0">
                                                            <svg
                                                                className="h-5 w-5 text-red-400"
                                                                viewBox="0 0 20 20"
                                                                fill="currentColor"
                                                            >
                                                                <path
                                                                    fillRule="evenodd"
                                                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                                                                    clipRule="evenodd"
                                                                />
                                                            </svg>
                                                        </div>
                                                        <div className="ml-3 flex-1">
                                                            <h3 className="text-sm font-medium text-red-800">
                                                                SSO Configuration Error
                                                            </h3>
                                                            <div className="mt-2 text-sm text-red-700">
                                                                {ticketError
                                                                    .split('\n')
                                                                    .map((line, index) => (
                                                                        <div key={index}>
                                                                            {line.startsWith(
                                                                                '• ',
                                                                            ) ? (
                                                                                <div className="ml-2 flex">
                                                                                    <span className="mr-2">
                                                                                        •
                                                                                    </span>
                                                                                    <span>
                                                                                        {line.substring(
                                                                                            2,
                                                                                        )}
                                                                                    </span>
                                                                                </div>
                                                                            ) : line.trim() ===
                                                                              'Suggested actions:' ? (
                                                                                <div className="mt-3 font-medium">
                                                                                    {line}
                                                                                </div>
                                                                            ) : line.trim() ? (
                                                                                <div
                                                                                    className={
                                                                                        index > 0
                                                                                            ? 'mt-1'
                                                                                            : ''
                                                                                    }
                                                                                >
                                                                                    {line}
                                                                                </div>
                                                                            ) : null}
                                                                        </div>
                                                                    ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Generated Ticket Display */}
                                    {ticketUrl && (
                                        <div className="border-t pt-6">
                                            <h4 className="text-sm font-semibold text-foreground mb-3">
                                                Generated SSO Configuration Link
                                            </h4>
                                            <div className="space-y-3">
                                                <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-md">
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-xs text-green-700 font-mono break-all">
                                                            {ticketUrl}
                                                        </p>
                                                    </div>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={handleCopyUrl}
                                                        className="flex-shrink-0 w-20 justify-center"
                                                    >
                                                        {copySuccess ? (
                                                            <>
                                                                <svg
                                                                    className="w-4 h-4 text-green-600"
                                                                    fill="currentColor"
                                                                    viewBox="0 0 20 20"
                                                                >
                                                                    <path
                                                                        fillRule="evenodd"
                                                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                                        clipRule="evenodd"
                                                                    />
                                                                </svg>
                                                                Copied!
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Copy className="w-4 h-4" />
                                                                Copy
                                                            </>
                                                        )}
                                                    </Button>
                                                </div>
                                                <div className="text-xs text-muted-foreground">
                                                    <p>
                                                        <strong>Instructions:</strong> Copy this
                                                        link and send it to the organization
                                                        administrator. The link expires in 5 days
                                                        and can only be used once.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Navigation Buttons */}
                                    <div className="flex justify-between pt-4">
                                        <Button
                                            variant="outline"
                                            onClick={handleBackToDomainAliases}
                                            disabled={!!ticketUrl}
                                        >
                                            Back
                                        </Button>
                                        {ticketUrl && (
                                            <Button
                                                onClick={handleSuccessfulCompletion}
                                                className="bg-green-600 hover:bg-green-700"
                                            >
                                                Done
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
